import * as React from 'react';

import type {
  DatesSetArg,
  EventClickArg,
  EventContentArg,
} from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { format } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';

import { $calendarDate, $calendarView } from '../_state/calendar.state';
import { $enabledLayers } from '../_state/preferences.state';
import { useColorResolver } from '../hooks/useColorResolver';
import type { CalendarEvent } from '../types/calendarEvent';
import { toFullCalendarEvent } from '../utils/timezoneHelpers';

import { CalendarEmptyState } from './CalendarEmptyState';
import { CalendarEventPopover } from './CalendarEventPopover';
import { ModuleEventContent } from './events/ModuleEventContent';

interface CalendarViewProps {
  events: CalendarEvent[];
}

export function CalendarView({ events }: CalendarViewProps) {
  const resolveColor = useColorResolver();

  const calendarRef = React.useRef<FullCalendar>(null);

  const [popoverEvent, setPopoverEvent] = React.useState<CalendarEvent | null>(
    null,
  );
  const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLElement | null>(
    null,
  );

  const calendarView = useAtomValue($calendarView);
  const [calendarDate, setCalendarDate] = useAtom($calendarDate);
  const enabledLayers = useAtomValue($enabledLayers);

  const fcEvents = React.useMemo(
    () =>
      events.map((event) => toFullCalendarEvent(event, resolveColor(event))),
    [events, resolveColor],
  );

  React.useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;

    const currentViewType = api.view.type;
    const currentStart = format(api.view.currentStart, 'yyyy-MM-dd');

    if (currentViewType !== calendarView) {
      api.changeView(calendarView);
    }
    if (calendarDate && currentStart !== calendarDate) {
      api.gotoDate(calendarDate);
    }
  }, [calendarView, calendarDate]);

  const handleDatesSet = React.useCallback(
    (dateInfo: DatesSetArg) => {
      const current = format(dateInfo.view.currentStart, 'yyyy-MM-dd');
      setCalendarDate(current);
    },
    [setCalendarDate],
  );

  const handleEventClick = React.useCallback((info: EventClickArg) => {
    const calendarEvent = info.event.extendedProps
      .calendarEvent as CalendarEvent;
    setPopoverEvent(calendarEvent);
    setPopoverAnchor(info.el);
  }, []);

  const renderEventContent = React.useCallback(
    (arg: EventContentArg) => {
      const calendarEvent = arg.event.extendedProps
        .calendarEvent as CalendarEvent;
      const color = resolveColor(calendarEvent);

      return <ModuleEventContent event={calendarEvent} color={color} />;
    },
    [resolveColor],
  );

  const handlePopoverClose = React.useCallback(() => {
    setPopoverEvent(null);
    setPopoverAnchor(null);
  }, []);

  const hasEnabledLayers = Object.values(enabledLayers).some(Boolean);

  if (!hasEnabledLayers) {
    return (
      <div className="@CalendarView h-full">
        <CalendarEmptyState variant="no-layers" />
      </div>
    );
  }

  return (
    <div className="@CalendarView h-full">
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView={calendarView}
        initialDate={calendarDate || undefined}
        events={fcEvents}
        datesSet={handleDatesSet}
        eventClick={handleEventClick}
        eventContent={renderEventContent}
        headerToolbar={false}
        height="100%"
      />

      <CalendarEventPopover
        event={popoverEvent}
        anchorEl={popoverAnchor}
        isOpen={!!popoverEvent}
        onClose={handlePopoverClose}
      />
    </div>
  );
}
